/**
 * Navegadores embutidos em apps (Instagram, Facebook, WhatsApp, TikTok…).
 *
 * É de onde vem o tráfego dos anúncios, e o Google recusa login OAuth dentro
 * deles (`disallowed_useragent`). Detectar serve para pôr o login por e-mail
 * na frente e, no botão do Google/Apple, explicar e oferecer "Abrir no
 * navegador".
 *
 * Por user agent — heurística: cada app se anuncia com uma marca própria, e
 * as marcas mudam com as versões. Na dúvida, NÃO é embutido: um falso
 * positivo só esconderia um botão que funcionaria.
 */

export type InAppName =
  | 'instagram'
  | 'facebook'
  | 'messenger'
  | 'threads'
  | 'whatsapp'
  | 'tiktok'
  | 'linkedin'
  | 'snapchat'
  | 'line'
  | 'twitter'
  | 'pinterest'
  | 'wechat'
  | 'webview';

export type MobileOs = 'android' | 'ios' | 'other';

export interface InAppBrowser {
  app: InAppName;
  /** O nome do app ("Instagram"); numa WebView desconhecida, "aplicativo". */
  label: string;
  os: MobileOs;
}

/** "dentro do Instagram" / "dentro deste aplicativo". */
export function insideApp({ app, label }: InAppBrowser): string {
  return app === 'webview' ? 'dentro deste aplicativo' : `dentro do ${label}`;
}

/**
 * Ordem importa: as marcas mais específicas primeiro (o Messenger também
 * carrega `FBAV`; o Threads, às vezes, a do Instagram).
 */
const MARKERS: [InAppName, string, RegExp][] = [
  ['messenger', 'Messenger', /FBAN\/Messenger|FB_IAB\/(?:MESSENGER|Orca-Android)|MessengerLite/i],
  // "Barcelona" é o codinome do Threads no user agent.
  ['threads', 'Threads', /\bBarcelona\b/],
  ['instagram', 'Instagram', /\bInstagram\b/i],
  ['facebook', 'Facebook', /FBAN\/|FBAV\/|FB_IAB\/|FBIOS|FB4A|\bFBSS\//],
  ['whatsapp', 'WhatsApp', /\bWhatsApp\b/i],
  ['tiktok', 'TikTok', /BytedanceWebview|musical_ly|\bTikTok\b|\btrill_|ByteLocale/i],
  ['linkedin', 'LinkedIn', /LinkedInApp/i],
  ['snapchat', 'Snapchat', /\bSnapchat\b/i],
  ['line', 'LINE', /\bLine\/\d/],
  ['twitter', 'X', /TwitterAndroid|Twitter for iP/i],
  ['pinterest', 'Pinterest', /\bPinterest\b/i],
  ['wechat', 'WeChat', /MicroMessenger/i],
];

/** WebView genérica do Android: o próprio sistema marca com `; wv)`. */
const ANDROID_WEBVIEW = /;\s*wv\)/;

export function mobileOs(ua: string): MobileOs {
  if (/android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  return 'other';
}

/** O app em que a página está aberta — ou `null` num navegador de verdade. */
export function detectInAppBrowser(ua: string | undefined | null): InAppBrowser | null {
  if (!ua) return null;
  const os = mobileOs(ua);
  for (const [app, label, marker] of MARKERS) {
    if (marker.test(ua)) return { app, label, os };
  }
  if (os === 'android' && ANDROID_WEBVIEW.test(ua)) return { app: 'webview', label: 'aplicativo', os };
  return null;
}

/**
 * Link que tira a página do app e abre no Chrome (Android). Sem o Chrome, o
 * `browser_fallback_url` carrega a mesma página — no pior caso nada muda.
 * Só http(s); o fragmento (#) não cabe numa URL `intent:`.
 */
export function androidIntentUrl(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const scheme = url.protocol.slice(0, -1);
  const target = `${url.host}${url.pathname}${url.search}`;
  const fallback = encodeURIComponent(`${url.origin}${url.pathname}${url.search}`);
  return `intent://${target}#Intent;scheme=${scheme};package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
}
